import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaFileWord, FaFilePdf } from 'react-icons/fa';
import { createApiClient } from '../../api/client';
import PickerModal from '../components/PickerModal';
import { FIR_LANGUAGES } from '../data/firLanguages';
import { CASE_TYPES } from '../data/caseTypes';
import { extractDocxText } from '../utils/docx';
import { LEGACY_FONTS, convertLegacyToUnicode, isLegacyFontSelected } from '../../utils/legacyFontConverter';
import '../mobile.css';

// Mirrors desktop's extractBetweenLabels (src/components/UploadDocument.js) -
// pulls the FIR narrative out of the raw OCR text returned for a scanned PDF.
const extractBetweenLabels = (fullText) => {
  const text = (fullText || '')
    .replace(/\r\n/g, '\n')
    .replace(/ /g, ' ')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n+/g, '\n')
    .trim();

  if (!text) return '';

  const startRe = /(first\s+information\s+contents|f\.?\s*i\.?\s*r\.?\s*contents)\s*/i;
  const endRe = /action\s+taken\s*/i;

  const startMatch = startRe.exec(text);
  if (!startMatch) return '';

  const afterStart = text.slice(startMatch.index + startMatch[0].length);
  const endMatch = endRe.exec(afterStart);
  if (!endMatch) return '';

  return afterStart.slice(0, endMatch.index).trim();
};

// Mirrors desktop's default-user-by-role logic (UploadDocument.js useEffect) -
// desktop keeps this silent (no picker shown to the user), just picks a sensible default.
const defaultAnalysisUser = () => {
  const role = localStorage.getItem('role');
  if (role === 'Advocate') return 'Defense Counsel';
  if (role === 'Police') return 'Investigation';
  return 'Accused';
};

const inputStyle = { width: '100%', boxSizing: 'border-box', borderWidth: 1, borderStyle: 'solid', borderColor: '#ccc', borderRadius: '8px', padding: '12px', marginBottom: '12px' };
const pickerStyle = { display: 'flex', justifyContent: 'space-between', border: '1px solid #ccc', borderRadius: '8px', padding: '12px', marginBottom: '12px', cursor: 'pointer' };

const NewCase = ({ API_URL }) => {
  const navigate = useNavigate();
  const wordFileInputRef = useRef(null);
  const pdfFileInputRef = useRef(null);

  const [caseType, setCaseType] = useState('');
  const [language, setLanguage] = useState('');
  const [policeStation, setPoliceStation] = useState('');
  const [legacyFont, setLegacyFont] = useState(LEGACY_FONTS[0]);
  const [sectionsEntry, setSectionsEntry] = useState('');
  const [complainantName, setComplainantName] = useState('');
  const [accusedNamesEntry, setAccusedNamesEntry] = useState('');
  const [firText, setFirText] = useState('');
  // Holds the last raw (un-converted) complaint text - either straight from
  // the Word file or straight from the user's typing/pasting - so switching
  // the Legacy Font picker re-converts from that original source instead of
  // re-running a conversion on text that may already be converted once.
  const rawFirTextRef = useRef('');

  const [showCaseTypePicker, setShowCaseTypePicker] = useState(false);
  const [showLanguagePicker, setShowLanguagePicker] = useState(false);
  const [showLegacyFontPicker, setShowLegacyFontPicker] = useState(false);
  const [showPdfConfirm, setShowPdfConfirm] = useState(false);

  const [isParsingFile, setIsParsingFile] = useState(false);
  const [parsingLabel, setParsingLabel] = useState('Reading document...');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const MAX_WORD_COUNT = 1000;
  const countWords = (text) => {
    const trimmed = (text || '').trim();
    return trimmed ? trimmed.split(/\s+/).length : 0;
  };
  const firWordCount = countWords(firText);
  const isOverWordLimit = firWordCount > MAX_WORD_COUNT;
  const [showWordLimitPopup, setShowWordLimitPopup] = useState(false);
  const wasOverWordLimitRef = useRef(false);

  useEffect(() => {
    if (isOverWordLimit && !wasOverWordLimitRef.current) {
      setShowWordLimitPopup(true);
    }
    wasOverWordLimitRef.current = isOverWordLimit;
  }, [isOverWordLimit]);

  const handleWordFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsParsingFile(true);
    setParsingLabel('Reading Word document...');
    setErrorMessage('');

    try {
      const text = await extractDocxText(file);
      rawFirTextRef.current = text;
      setFirText(isLegacyFontSelected(legacyFont) ? convertLegacyToUnicode(text, legacyFont) : text);
    } catch (err) {
      setErrorMessage(err.message || 'Failed to read the Word document.');
    } finally {
      setIsParsingFile(false);
      if (wordFileInputRef.current) wordFileInputRef.current.value = '';
    }
  };

  const callPdfOcrApi = async (file) => {
    const form = new FormData();
    form.append('file', file);
    form.append('language', language);

    const api = createApiClient(API_URL);
    const response = await api.post('/fir/pdf/ocr', form);
    return response?.data?.firText || '';
  };

  // Converts pasted text on the way in when a legacy font is selected - the
  // picker-driven reconversion only fires on a font *change*, so without
  // this a paste (e.g. copying a paragraph out of an old Kruti Dev document)
  // after the font was already selected would silently stay unconverted.
  const handleFirTextPaste = (e) => {
    const pasted = (e.clipboardData || window.clipboardData).getData('text');
    if (!pasted) return;
    e.preventDefault();

    const textarea = e.target;
    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;

    const convertedPasted = isLegacyFontSelected(legacyFont) ? convertLegacyToUnicode(pasted, legacyFont) : pasted;
    const newDisplayed = firText.slice(0, selectionStart) + convertedPasted + firText.slice(selectionEnd);

    // Best-effort: mirror the same splice into the raw baseline using the
    // original (un-converted) pasted text, so a later font switch still
    // reconverts from the true source. Exact when raw/displayed are still
    // aligned (nothing converted yet, or pasting at the end); approximate
    // otherwise, since conversion can change text length.
    const raw = rawFirTextRef.current;
    const rawStart = Math.min(selectionStart, raw.length);
    const rawEnd = Math.min(selectionEnd, raw.length);
    rawFirTextRef.current = raw.slice(0, rawStart) + pasted + raw.slice(rawEnd);

    setFirText(newDisplayed);

    const newCursorPos = selectionStart + convertedPasted.length;
    setTimeout(() => textarea.setSelectionRange(newCursorPos, newCursorPos), 0);
  };

  const handlePdfFileClick = () => {
    if (!language) {
      setErrorMessage('Please select the language before uploading the document.');
      return;
    }
    setErrorMessage('');
    setShowPdfConfirm(true);
  };

  const handlePdfConfirmContinue = () => {
    setShowPdfConfirm(false);
    setTimeout(() => pdfFileInputRef.current?.click(), 0);
  };

  const handlePdfFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setErrorMessage('');
    setIsParsingFile(true);
    setParsingLabel('Uploading PDF for OCR. Please wait...');

    try {
      const firTextFromOcr = await callPdfOcrApi(file);
      if (!firTextFromOcr.trim()) {
        setErrorMessage('Could not read the FIR Contents.');
        return;
      }

      setParsingLabel('Extracting FIR contents...');
      const extracted = extractBetweenLabels(firTextFromOcr);
      if (!extracted) {
        setErrorMessage('Could not read the FIR Contents.');
        return;
      }

      setFirText(extracted);
    } catch (err) {
      setErrorMessage(err?.response?.data?.error || err?.response?.data?.message || 'Failed to process the PDF. Please try again.');
    } finally {
      setIsParsingFile(false);
      if (pdfFileInputRef.current) pdfFileInputRef.current.value = '';
    }
  };

  const validate = () => {
    if (!caseType) return 'Select the case type.';
    if (!language) return 'Select the FIR language.';
    if (!sectionsEntry) return 'Enter the FIR sections.';
    if (!complainantName) return 'Enter the complainant name.';
    if (!accusedNamesEntry) return 'Enter the accused names. If the accused name is not known, enter Unknown.';
    if (!firText.trim()) return 'Enter the FIR contents.';
    if (isOverWordLimit) return `The free version supports a complaint of up to ${MAX_WORD_COUNT} words.`;
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const textToSubmit = `Sections ${sectionsEntry}\nComplainant: ${complainantName}\nAccused Person(s): ${accusedNamesEntry}\nComplaint:\n${firText}`;

      const form = new FormData();
      form.append('user', defaultAnalysisUser());
      form.append('language', language);
      form.append('caseType', caseType);
      form.append('policeStation', policeStation);
      form.append('sections', sectionsEntry);
      form.append('complainant', complainantName);
      form.append('accusedList', accusedNamesEntry);
      form.append('firText', textToSubmit);

      const api = createApiClient(API_URL);
      await api.post('/fir', form);
      navigate('/m/cases', { state: { reloadCases: true } });
    } catch (err) {
      setErrorMessage(err?.response?.data?.error || err?.response?.data?.message || 'Failed to upload FIR. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#094268', marginBottom: '15px' }}>Analyze FIR</h2>

      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#094268', marginBottom: '6px' }}>Case Type</label>
      <div style={{ ...pickerStyle, cursor: 'text' }}>
        <input
          placeholder="Case Type"
          value={caseType}
          onChange={(e) => setCaseType(e.target.value)}
          style={{ border: 'none', outline: 'none', flex: 1, padding: 0, background: 'transparent', color: caseType ? '#000' : '#999', height: '20px', }}
        />
        <span onClick={() => setShowCaseTypePicker(true)} style={{ color: '#0A426A', fontWeight: 600, cursor: 'pointer' }}>Select</span>
      </div>

      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#094268', marginBottom: '6px' }}>Complaint Language</label>
      <div onClick={() => setShowLanguagePicker(true)} style={pickerStyle}>
        <span style={{ color: language ? '#000' : '#999' }}>{language || 'Complaint Language'}</span>
        <span style={{ color: '#0A426A', fontWeight: 600 }}>Select</span>
      </div>

      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#094268', marginBottom: '6px' }}>Legacy Font</label>
      <div
        onClick={() => setShowLegacyFontPicker(true)}
        style={pickerStyle}
      >
        <span style={{ color: legacyFont !== LEGACY_FONTS[0] ? '#000' : '#999' }}>{legacyFont}</span>
        <span style={{ color: '#0A426A', fontWeight: 600 }}>Select</span>
      </div>

      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#094268', marginBottom: '6px' }}>Sections</label>
      <input
        placeholder="Sections (e.g. 318 BNS; 66(C) IT Act)"
        value={sectionsEntry}
        onChange={(e) => setSectionsEntry(e.target.value)}
        style={inputStyle}
      />

      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#094268', marginBottom: '6px' }}>Complainant Name</label>
      <input
        placeholder="Complainant Name"
        value={complainantName}
        onChange={(e) => setComplainantName(e.target.value)}
        style={inputStyle}
      />

      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#094268', marginBottom: '6px' }}>Accused Persons</label>
      <input
        placeholder="Accused Persons (separate with ,)"
        value={accusedNamesEntry}
        onChange={(e) => setAccusedNamesEntry(e.target.value)}
        style={inputStyle}
      />

      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#094268', marginBottom: '6px' }}>Complaint</label>
      <div style={{ position: 'relative' }}>
        <textarea
          placeholder="Upload complaint from PDF or Word, or type/paste the FIR contents here."
          value={firText}
          onChange={(e) => {
            setFirText(e.target.value);
            rawFirTextRef.current = e.target.value;
          }}
          onPaste={handleFirTextPaste}
          disabled={isParsingFile}
          rows={12}
          style={{ ...inputStyle, fontFamily: 'inherit', fontSize: '14px', opacity: isParsingFile ? 0.5 : 1 }}
        />

        {isParsingFile && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.6)' }}>
            <div style={{ textAlign: 'center' }}>
              <div className="m-spinner" style={{ margin: '0 auto', borderTopColor: '#094268' }} />
              <p style={{ marginTop: '8px', fontWeight: 600 }}>{parsingLabel}</p>
            </div>
          </div>
        )}
      </div>

      <p style={{ marginTop: '-6px', marginBottom: '12px', fontSize: '13px', textAlign: 'right', color: isOverWordLimit ? '#D32F2F' : '#666' }}>
        {firWordCount} / {MAX_WORD_COUNT} words
      </p>

      <input ref={pdfFileInputRef} type="file" accept="application/pdf,.pdf" onChange={handlePdfFileSelected} style={{ display: 'none' }} />
      <input ref={wordFileInputRef} type="file" accept=".docx" onChange={handleWordFileSelected} style={{ display: 'none' }} />

      <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
        <button
          type="button"
          onClick={handlePdfFileClick}
          disabled={isParsingFile || loading}
          className="m-pill-btn"
          style={{ color: '#D32F2F', fontWeight: 600, gap: '8px' }}
        >
          <FaFilePdf size={16} /> PDF
        </button>
        <button
          type="button"
          onClick={() => wordFileInputRef.current?.click()}
          disabled={isParsingFile || loading}
          className="m-pill-btn"
          style={{ color: '#2B579A', fontWeight: 600, gap: '8px' }}
        >
          <FaFileWord size={16} /> Word
        </button>

      </div>

      <button onClick={handleSubmit} disabled={loading || isParsingFile || isOverWordLimit} className="m-button-primary">
        {loading ? <div className="m-spinner" style={{ margin: '0 auto' }} /> : 'Submit'}
      </button>

      {loading && (
        <p style={{ textAlign: 'center', fontSize: '14px', marginTop: '10px' }}>
          <strong>Please wait. AI Agent is analyzing FIR - this may take about a minute depending on the complexity of the complaint.</strong>
        </p>
      )}

      {showCaseTypePicker && (
        <PickerModal title="Case Type" options={CASE_TYPES} selected={caseType} onSelect={setCaseType} onClose={() => setShowCaseTypePicker(false)} />
      )}

      {showLanguagePicker && (
        <PickerModal title="Complaint Language" options={FIR_LANGUAGES} selected={language} onSelect={setLanguage} onClose={() => setShowLanguagePicker(false)} />
      )}

      {showLegacyFontPicker && (
        <PickerModal
          title="Legacy Font"
          options={LEGACY_FONTS}
          selected={legacyFont}
          onSelect={(newFont) => {
            setLegacyFont(newFont);
            if (isLegacyFontSelected(newFont) && rawFirTextRef.current.trim() !== '') {
              setFirText(convertLegacyToUnicode(rawFirTextRef.current, newFont));
            } else if (newFont === LEGACY_FONTS[0]) {
              // Not Applicable - restore the original text as first pasted/extracted, undoing any conversion.
              setFirText(rawFirTextRef.current);
            }
          }}
          onClose={() => setShowLegacyFontPicker(false)}
        />
      )}

      {showPdfConfirm && (
        <div className="m-popup-overlay">
          <div className="m-popup-container">
            <h3>PDF Upload</h3>
            <p style={{ textAlign: 'justify' }}>Max 4 pages only. Only computer-typed PDF documents can be read. Handwritten or improperly scanned PDFs may not work.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setShowPdfConfirm(false)} className="m-pill-btn" style={{ color: '#094268', fontWeight: 600 }}>Cancel</button>
              <button className="m-button-primary" style={{ width: 'auto', padding: '8px 14px' }} onClick={handlePdfConfirmContinue}>Continue</button>
            </div>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="m-popup-overlay">
          <div className="m-popup-container">
            <h3>Error</h3>
            <p>{errorMessage}</p>
            <button className="m-button-primary" onClick={() => setErrorMessage('')}>OK</button>
          </div>
        </div>
      )}

      {showWordLimitPopup && (
        <div className="m-popup-overlay">
          <div className="m-popup-container">
            <h3>Word Limit Reached</h3>
            <p>The free version supports a complaint of up to {MAX_WORD_COUNT} words. Please shorten the text to continue, or contact us for a paid plan with a higher limit.</p>
            <button className="m-button-primary" onClick={() => setShowWordLimitPopup(false)}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewCase;
